import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "@/i18n/IntlProvider";
import { api } from "@/lib/api";

type HistoryEntry = {
  id: string;
  eventType: string;
  occurredAt: string;
  actorAccountId: string | null;
  previousState: Record<string, unknown> | null;
  newState: Record<string, unknown> | null;
  note: string | null;
};

type HistoryListResponse = {
  entries: HistoryEntry[];
};

/**
 * Verlauf-Card — chronological audit log of every lifecycle mutation
 * on the code. Each row carries a localized event label + an inline
 * diff renderer per event type (URL change shows alt → neu; password
 * set just shows the activation; etc.). Backend writes one row per
 * mutation via [QrCodeService.recordHistory], so deletes never appear
 * but everything else does.
 *
 * Renders for both STATIC and DYNAMIC codes — dynamic gets the rich
 * URL-change history; static still shows create / lock / design-edit
 * which is useful for audit purposes regardless of mode.
 */
export function CodeHistoryCard({ codeId }: { codeId: string }) {
  const t = useTranslations("app.codes.detail.history");

  const query = useQuery({
    queryKey: ["code", codeId, "history"],
    queryFn: () =>
      api<HistoryListResponse>(`/v1/codes/${codeId}/history?limit=20`),
    retry: false,
    staleTime: 15_000,
  });

  const entries = query.data?.entries ?? [];

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <h2 className="text-sm font-semibold">{t("title")}</h2>
      {query.isLoading ? (
        <p className="text-xs text-muted-foreground">{t("loading")}</p>
      ) : entries.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("empty")}</p>
      ) : (
        <ol className="space-y-2.5">
          {entries.map((e) => (
            <li
              key={e.id}
              className="grid grid-cols-[auto_1fr] items-baseline gap-3 border-b border-border/40 pb-2.5 last:border-b-0 last:pb-0"
            >
              <span
                className="whitespace-nowrap text-xs tabular-nums text-muted-foreground"
                title={t("timeTooltip")}
              >
                {formatTime(e.occurredAt)}
              </span>
              <div className="space-y-0.5">
                <div className="text-xs font-medium">
                  {t(`event.${e.eventType}`)}
                </div>
                <HistoryDiff entry={e} />
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/**
 * Per-event-type renderer for the diff line. The backend deliberately
 * keeps `previousState` / `newState` shape-loose so we can extend it
 * without a wire-breaking migration; this switch translates each shape
 * back into a human-readable line. Unknown event types fall through to
 * a quiet "—" so a future backend event type lands without a UI crash.
 */
function HistoryDiff({ entry }: { entry: HistoryEntry }) {
  const t = useTranslations("app.codes.detail.history");
  const prev = entry.previousState ?? {};
  const next = entry.newState ?? {};

  switch (entry.eventType) {
    case "redirect_target_edited":
    case "payload_edited":
      return (
        <div className="space-y-0.5 text-xs">
          <div className="text-muted-foreground">
            {t("from")}: <span className="font-mono break-all">{String(prev.payload ?? "—")}</span>
          </div>
          <div className="text-muted-foreground">
            {t("to")}: <span className="font-mono break-all">{String(next.payload ?? "—")}</span>
          </div>
        </div>
      );
    case "expiry_set":
      return (
        <div className="text-xs text-muted-foreground">
          {t("to")}:{" "}
          <span className="tabular-nums">
            {next.expiresAt ? formatTime(String(next.expiresAt)) : "—"}
          </span>
        </div>
      );
    case "expiry_cleared":
      return (
        <div className="text-xs text-muted-foreground">{t("expiryCleared")}</div>
      );
    case "design_edited":
      return (
        <div className="text-xs text-muted-foreground">
          {summariseDesign(prev.design as Record<string, unknown> | undefined)} →{" "}
          {summariseDesign(next.design as Record<string, unknown> | undefined)}
        </div>
      );
    case "created":
      return (
        <div className="text-xs text-muted-foreground">
          {t("createdAs", { mode: String(next.mode ?? "?") })}
          {next.payload ? (
            <>
              {" — "}
              <span className="font-mono break-all">{String(next.payload)}</span>
            </>
          ) : null}
        </div>
      );
    case "converted_to_dynamic":
      return (
        <div className="text-xs text-muted-foreground">
          {t("slug")}:{" "}
          <span className="font-mono">{String(next.shortSlug ?? "—")}</span>
        </div>
      );
    case "locked":
    case "password_set":
    case "password_removed":
    case "archived":
      return null;
    default:
      return null;
  }
}

function summariseDesign(d: Record<string, unknown> | undefined): string {
  if (!d) return "—";
  const fg = String(d.foreground ?? "");
  const frame = String(d.frame ?? "");
  return `${fg} · ${frame}`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
